import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { POSTS } from "../lib/blog";
import { useSEO } from "../hooks/useSEO";
import InstallBadgesRow from "../components/InstallBadgesRow";
import { ArrowRight, Clock, Calendar } from "lucide-react";

/**
 * BlogIndexPage — public /blog route. Lists all SEO posts.
 * Renders fully on initial HTML so crawlers index it.
 */
export default function BlogIndexPage() {
  const { language } = useApp();
  const es = language === "es";

  useSEO({
    title: es
      ? "Blog · BScanIA — IA emocional, decisiones y liderazgo"
      : "Blog · BScanIA — Emotional AI, decisions and leadership",
    description: es
      ? "Artículos sobre cómo tomar decisiones difíciles con claridad emocional, IA para ejecutivos y managers, inteligencia emocional con IA y crecimiento profesional."
      : "Articles on making hard decisions with emotional clarity, AI for executives and managers, emotional intelligence with AI, and professional growth.",
    canonical: `https://bscan.app/blog${es ? "" : "?lang=en"}`,
    lang: language,
    keywords: es
      ? "blog IA, IA para decisiones, mentor IA, coach emocional IA, IA para ejecutivos, IA para managers, inteligencia emocional IA, BScanIA"
      : "AI blog, decision making AI, AI mentor, AI emotional coach, AI for executives, AI for managers, emotional intelligence AI, BScanIA",
  });

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(es ? "es-ES" : "en-US", {
      year: "numeric", month: "long", day: "numeric",
    });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="blog-index-page">
      {/* Header bar — keep it light, link back home */}
      <header className="border-b border-white/10 px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="blog-logo-link">
          <img src="/b-icon.png" alt="BScanIA" className="w-9 h-9 rounded-md" />
          <span className="font-display font-bold text-lg">BScanIA</span>
        </Link>
        <Link
          to="/register"
          className="font-mono text-xs uppercase tracking-[0.15em] text-[#006CFF] hover:text-white transition-colors"
          data-testid="blog-cta-link"
        >
          {es ? "Empieza gratis →" : "Start free →"}
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-3">
          / {es ? "Blog" : "Blog"}
        </div>
        <h1 className="font-display font-bold text-4xl lg:text-6xl mb-4 leading-tight">
          {es ? "Ideas para decidir mejor." : "Ideas to decide better."}
        </h1>
        <p className="text-white/65 text-lg lg:text-xl max-w-2xl mb-16">
          {es
            ? "Artículos sobre IA emocional, decisiones difíciles, liderazgo y patrones de comportamiento — el mismo enfoque con el que B trabaja contigo."
            : "Articles on emotional AI, hard decisions, leadership and behavior patterns — the same lens B uses with you."}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {POSTS.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug[language] || post.slug.es}`}
              className="group block rounded-2xl border border-white/10 bg-[#121214] overflow-hidden hover:border-[#006CFF]/40 transition-colors"
              data-testid={`blog-card-${post.id}`}
            >
              <div className="aspect-[16/9] bg-black overflow-hidden">
                <img
                  src={post.hero}
                  alt={post.title[language] || post.title.es}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-5 lg:p-6">
                <div className="flex items-center gap-3 mb-3 font-mono text-[10px] uppercase tracking-[0.15em] text-white/45">
                  <span className="text-[#006CFF]">/ {post.category[language] || post.category.es}</span>
                  <span>·</span>
                  <Calendar className="w-3 h-3" />
                  <span>{fmtDate(post.date)}</span>
                  <span>·</span>
                  <Clock className="w-3 h-3" />
                  <span>{post.readingTime} {es ? "min" : "min"}</span>
                </div>
                <h2 className="font-display font-semibold text-xl lg:text-2xl leading-snug mb-3 group-hover:text-[#00C8FF] transition-colors">
                  {post.title[language] || post.title.es}
                </h2>
                <p className="text-white/65 text-sm leading-relaxed line-clamp-3">
                  {post.description[language] || post.description.es}
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-[#006CFF] group-hover:gap-3 transition-all">
                  {es ? "Leer" : "Read"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-20 p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-[#006CFF]/15 via-[#7C3AED]/8 to-transparent border border-[#006CFF]/25 text-center">
          <h3 className="font-display font-bold text-2xl lg:text-3xl mb-3">
            {es ? "Listo para hablar con B?" : "Ready to talk to B?"}
          </h3>
          <p className="text-white/65 mb-6 max-w-lg mx-auto">
            {es
              ? "7 días gratis. Sin tarjeta de crédito. Cancela cuando quieras."
              : "7 days free. No credit card. Cancel anytime."}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#006CFF] hover:bg-[#0052CC] text-white font-display font-semibold transition-colors"
            data-testid="blog-footer-cta"
          >
            {es ? "Empieza ahora" : "Start now"}
            <ArrowRight className="w-4 h-4" />
          </Link>

          {/* Install badges */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <InstallBadgesRow variant="default" align="center" utmSource="blog_index" />
          </div>
        </div>
      </main>
    </div>
  );
}
